import { norm } from './search';
import type { Pdf, UnitStatus } from './schema';
import { isTeams, isUrl, isYouTube } from './urls';

/** Import columns. `he` is the canonical header; `alt` are accepted aliases (compared after normalization). */
export const IMPORT_COLUMNS = [
  { key: 'name', he: 'שם היחידה', alt: ['name', 'שם'], example: 'פרקליטות מחוז צפון (פלילי)' },
  { key: 'nameAr', he: 'שם בערבית', alt: ['arabic', 'name ar', 'שם ערבית'], example: '' },
  { key: 'nameEn', he: 'שם באנגלית', alt: ['english', 'name en', 'שם אנגלית'], example: '' },
  { key: 'domain', he: 'תחום', alt: ['domain', 'תחום פעילות'], example: 'פלילי' },
  { key: 'region', he: 'אזור', alt: ['region', 'אזור גאוגרפי', 'מיקום'], example: 'חיפה והצפון' },
  {
    key: 'description',
    he: 'תיאור',
    alt: ['description', 'תיאור היחידה'],
    example: 'מייצגת את המדינה בהליכים פליליים במחוז.',
  },
  { key: 'keywords', he: 'מילות מפתח', alt: ['keywords'], example: 'תביעה; ליטיגציה' },
  {
    key: 'pdfs',
    he: 'קישורי PDF',
    alt: ['pdfs', 'pdf', 'מסמכים'],
    example: 'דף מידע::https://example.com/demo/north.pdf',
  },
  { key: 'youtube', he: 'סרטון YouTube', alt: ['youtube', 'סרטון'], example: '' },
  {
    key: 'teams',
    he: 'קישור Teams',
    alt: ['teams', 'teams link'],
    example: 'https://teams.microsoft.com/l/meetup-join/DEMO-NORTH',
  },
  { key: 'status', he: 'סטטוס', alt: ['status', 'מצב פרסום'], example: 'טיוטה' },
] as const;

export type ImportKey = (typeof IMPORT_COLUMNS)[number]['key'];

/** Parses CSV or tab-separated text (pasted from Excel). Delimiter is sniffed from the header row. */
export function parseCSV(input: string): string[][] {
  const text = String(input).replace(/^\uFEFF/, '');
  const first = text.split(/\r?\n/)[0] || '';
  const count = (ch: string) => first.split(ch).length - 1;
  const delim = count('\t') > count(',') ? '\t' : count(';') > count(',') ? ';' : ',';

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === delim) {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((x) => String(x).trim() !== ''));
}

const csvCell = (v: string) => (/[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);

/** Header + one example row + one blank draft row. Prefix with a BOM when saving so Excel reads Hebrew. */
export function csvTemplate(): string {
  return [
    IMPORT_COLUMNS.map((c) => c.he).join(','),
    IMPORT_COLUMNS.map((c) => csvCell(c.example)).join(','),
    [
      'פרקליטות מחוז צפון (אזרחי)',
      '',
      '',
      'אזרחי',
      'חיפה והצפון',
      '',
      '',
      '',
      '',
      '',
      'טיוטה',
    ].join(','),
  ].join('\n');
}

const STATUS_WORDS: Record<UnitStatus, string[]> = {
  published: ['מפורסם', 'מפורסמת', 'published', 'פורסם'],
  draft: ['טיוטה', 'draft'],
  hidden: ['מוסתר', 'מוסתרת', 'hidden'],
};
// Compared after normalization, which folds final letters (ם -> מ), so the lists must be normalized too.
const STATUS_LOOKUP = new Map<string, UnitStatus>(
  (Object.entries(STATUS_WORDS) as [UnitStatus, string[]][]).flatMap(([status, words]) =>
    words.map((w): [string, UnitStatus] => [norm(w), status]),
  ),
);

function parseStatus(v: string): { s: UnitStatus; warn?: string } {
  const n = norm(v);
  if (!n) return { s: 'draft' };
  const hit = STATUS_LOOKUP.get(n);
  if (hit) return { s: hit };
  return { s: 'draft', warn: `סטטוס לא מוכר "${v}", יוגדר כטיוטה` };
}

export interface ImportedUnit {
  name: string;
  nameAr: string;
  nameEn: string;
  domain: string;
  region: string;
  description: string;
  keywords: string[];
  pdfs: Pdf[];
  youtube: string;
  teams: string;
  status: UnitStatus;
}

export interface ImportRow {
  /** 1-based line number in the source file, header being line 1. */
  line: number;
  unit: ImportedUnit;
  errs: string[];
  warns: string[];
  /** Id of the existing unit this row will update (matched by normalized name), or null for a new unit. */
  existing: string | null;
}

export type ImportAnalysis = { error: string } | { rows: ImportRow[]; unknown: string[] };

export interface ImportContext {
  domains: string[];
  regions: string[];
  units: { id: string; name: string }[];
}

/** Validates parsed rows against the current data. Rows with `errs` are skipped on commit. */
export function analyzeImport(rows: string[][], ctx: ImportContext): ImportAnalysis {
  if (!rows.length) return { error: 'הקובץ ריק.' };
  const header = rows[0]!.map(norm);
  const idx: Partial<Record<ImportKey, number>> = {};
  for (const c of IMPORT_COLUMNS) {
    const names = [c.he, ...c.alt].map(norm);
    const i = header.findIndex((h) => names.includes(h));
    if (i >= 0) idx[c.key] = i;
  }
  if (idx.name == null) {
    return {
      error:
        'לא נמצאה עמודה בשם "שם היחידה" בשורה הראשונה. העתיקו את התבנית והדביקו מתחתיה את הנתונים.',
    };
  }
  const used = new Set(Object.values(idx));
  const unknown = rows[0]!.filter((h, i) => String(h).trim() && !used.has(i));

  const seen = new Map<string, number>();
  const out: ImportRow[] = [];
  rows.slice(1).forEach((r, ri) => {
    const line = ri + 2;
    const g = (k: ImportKey) => {
      const i = idx[k];
      return i == null ? '' : String(r[i] ?? '').trim();
    };
    const st = parseStatus(g('status'));
    const pdfs: Pdf[] = g('pdfs')
      .split(/\s*\|\s*|\n/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => {
        const m = x.split('::');
        return m.length > 1
          ? { title: m[0]!.trim(), url: m.slice(1).join('::').trim() }
          : { title: 'מסמך מידע', url: x };
      });
    const unit: ImportedUnit = {
      name: g('name'),
      nameAr: g('nameAr'),
      nameEn: g('nameEn'),
      domain: g('domain'),
      region: g('region'),
      description: g('description'),
      keywords: g('keywords')
        .split(/[;,،]/)
        .map((x) => x.trim())
        .filter(Boolean),
      pdfs,
      youtube: g('youtube'),
      teams: g('teams'),
      status: st.s,
    };

    const errs: string[] = [];
    const warns: string[] = [];
    const key = norm(unit.name);
    if (!unit.name) errs.push('חסר שם יחידה');
    else if (seen.has(key)) errs.push(`מופיעה פעמיים בקובץ (גם בשורה ${seen.get(key)})`);
    else seen.set(key, line);
    if (unit.teams && !isTeams(unit.teams)) errs.push('קישור Teams לא תקין');
    if (unit.youtube && !isYouTube(unit.youtube)) errs.push('קישור YouTube לא תקין');
    if (pdfs.some((p) => !isUrl(p.url))) errs.push('קישור PDF לא תקין');
    if (st.warn) warns.push(st.warn);
    if (!unit.teams) warns.push('ללא קישור Teams');
    if (unit.domain && !ctx.domains.includes(unit.domain))
      warns.push(`תחום חדש יתווסף: ${unit.domain}`);
    if (unit.region && !ctx.regions.includes(unit.region))
      warns.push(`אזור חדש יתווסף: ${unit.region}`);

    const existing = key ? ctx.units.find((u) => norm(u.name) === key) : undefined;
    out.push({ line, unit, errs, warns, existing: existing ? existing.id : null });
  });
  return { rows: out, unknown };
}
